import {
    Box,
    Button,
    Container,
    Flex,
    HStack,
    Icon,
    IconButton,
    Text,
    useColorMode,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import {keyframes} from "@emotion/react";
import {Link, useLocation} from "react-router-dom";
import {Icons} from "./Icons";
import {useCatalogItemStore} from "../store/catalogItem";
import {useShallow} from "zustand/react/shallow";

const Navbar = () => {
    const {colorMode, toggleColorMode} = useColorMode();
    const toast = useToast();
    const location = useLocation();

    const {resetCatalogItems, isLoading} = useCatalogItemStore(
        useShallow((s) => ({
            resetCatalogItems: s.resetCatalogItems,
            isLoading: s.isLoading,
        })),
    );

    const pulseGradient = keyframes`
        0% {
            background-position: 0 50%;
            filter: brightness(1);
        }
        50% {
            background-position: 100% 50%;
            filter: brightness(1.12);
        }
        100% {
            background-position: 0 50%;
            filter: brightness(1);
        }
    `;

    const bg = useColorModeValue("whiteAlpha.800", "blackAlpha.400");
    const border = useColorModeValue("gray.200", "whiteAlpha.200");

    const onReset = async (): Promise<void> => {
        const r = await resetCatalogItems();
        toast({
            title: r.success ? "Demo reset" : "Reset failed",
            description: r.message,
            status: r.success ? "success" : "error",
            isClosable: true,
        });
    };

    return (
        <Box
            as="header"
            position="sticky"
            top={0}
            zIndex={10}
            bg={bg}
            borderBottomWidth="1px"
            borderColor={border}
            backdropFilter="blur(12px)"
        >
            <Container maxW="1140px" px={4}>
                <Flex
                    h={16}
                    alignItems="center"
                    justifyContent="space-between"
                    gap={3}
                    flexDir={{base: "column", sm: "row"}}
                    py={{base: 3, sm: 0}}
                >
                    {/* Box instead of Text to avoid <p> nesting issues */}
                    <Box
                        fontSize={{base: "22", sm: "28"}}
                        fontWeight="bold"
                        letterSpacing="0.06em"
                        textTransform="uppercase"
                        textAlign="center"
                    >
                        <Link to="/" style={{textDecoration: "none"}}>
                            <HStack spacing={2} alignItems="center" justifyContent="center">
                                <Icon as={Icons.Catalog} boxSize={5} color={useColorModeValue("#FFB800", "#FF2DAA")}/>
                                <Text
                                    as="span"
                                    display="inline-block"
                                    bgGradient="linear(to-r, #FFB800, #FF2DAA, #FFB800)"
                                    bgClip="text"
                                    bgSize="200% 200%"
                                    letterSpacing="0.06em"
                                    animation={`${pulseGradient} 6s ease-in-out infinite`}
                                >
                                    Khagen.dev Catalog Demo
                                </Text>
                            </HStack>
                        </Link>
                    </Box>

                    <HStack spacing={2} alignItems="center">
                        <Button
                            variant="outline"
                            onClick={() => void onReset()}
                            isLoading={isLoading}
                            leftIcon={<Icons.RotateCcw size={16}/>}
                            size="sm"
                        >
                            Reset demo
                        </Button>

                        <Link to="/create">
                            <IconButton
                                aria-label="Add item"
                                icon={<Icons.Add size={18}/>}
                                variant={location.pathname === "/create" ? "solid" : "ghost"}
                                size="sm"
                            />
                        </Link>

                        <IconButton
                            onClick={toggleColorMode}
                            aria-label="Toggle color mode"
                            icon={colorMode === "light" ? <Icons.Dark size={18}/> : <Icons.Light size={18}/>}
                            variant="ghost"
                            size="sm"
                        />
                    </HStack>
                </Flex>
            </Container>
        </Box>
    );
};

export default Navbar;